<template>
  <div class="builder-nav--items">
    <header class="outline-bottom light--border">

      <v-select
        v-if="filterOptions.length"
        :value="category"
        @change="$emit('update:category', $event)"
        :items="filterOptions"
        item-text="text"
        item-value="value"
        dense
        outlined
        hide-details
        label="Filter"
        class="mr-2"
        style="max-width: 140px;"
      ></v-select>

      <input
        type="text"
        v-model="search"
        autocomplete="off"
        placeholder="Search models..."
        aria-label="Search component models"
        class="outline light--border"
      />

      <v-icon class="mx-2">
        mdi-magnify
      </v-icon>

    </header>
    <div
      ref="group"
      class="v-item-group fill-height autoscroll"
    >
      <div v-for="( item, position ) in visibleList" :key="`${type}:${item.id}`" class="bike-item-card">
      <button
        type="button"
        :aria-pressed="value === item.index"
        class="bike-item body-1"
        :class="{ selected: value === item.index, thumb: item.thumb, 'large-product': ['Groupset', 'Saddles'].includes(type) }"
        @click="$emit( 'input', item.index )"
      >

        <v-avatar v-if="value === item.index" class="bike-item-icon" size="30" color="bb-primary">
          <v-icon small dark>mdi-check-bold</v-icon>
        </v-avatar>

        <div
          class="bike-item-image"
          :class="{ 'has-brand': item.brandLogo }"
        >
          <img v-if="item.brandLogo" class="bike-item-brand" loading="lazy" decoding="async" :class="{ 'tyre-brand': type === 'Tyres' && item.brandLogo.startsWith('https:') }" :src="item.brandLogo" :alt="item.brand">
          <img v-if="item.src && !unavailable[item.src]" class="bike-item-product" :loading="position < 3 ? 'eager' : 'lazy'" decoding="async" :src="item.src" alt="" @error="$set(unavailable, item.src, true)">
          <span v-else-if="item.noCages || item.noPedals" class="bike-item-unavailable" aria-hidden="true">—</span>
          <span v-else class="bike-item-unavailable">Image unavailable</span>
        </div>

        <div class="bike-item-info-name">
          {{ item.name }}
        </div>

        <div class="bike-item-info-price" v-if="item.info.price || item.noCages || item.noPedals || item.name === 'SEKA Gladius' || isExtremaCockpit(item.name)">
          <template v-if="item.name === 'SEKA Gladius'">Included with Exaero RDC</template>
          <template v-else-if="isExtremaCockpit(item.name)">Included with the frameset</template>
          <template v-else>
          <span v-if="item.info.priceFrom">From&nbsp;</span>
          <template v-if="item.info.pair">2 &times; {{ item.info.unitPrice }} € = </template>{{ item.info.price }} €
          </template>
        </div>

        <div v-if="item.delta" class="bike-item-info-delta" :class="{ win: item.delta.win }" :title="item.delta.win ? 'Lighter for less than your current choice' : 'Compared with your current choice'">
          {{ item.delta.label }}
        </div>

        <div class="bike-item-info-colors"  v-if="item.info.colors.length > 1">
          <Color
            v-for="( color, i ) in item.info.colors"
            :key="i"
            :value="[ color.color, color.color2 ]"
            class="mb-0"
            small
          />
          <span v-if="item.info.more">
            +{{ item.info.more }}
          </span>

        </div>

        <BtnSelect :active="value === item.index"/>

      </button>
      <button v-if="!item.noCages && !item.noPedals" type="button" class="bike-item-description" :aria-label="`Description of ${item.name}`" @click="$emit('description', { ...items[item.index], step: { title: type } })"><v-icon>mdi-information-outline</v-icon></button>
      </div>
    </div>
  </div>
</template>

<script>

  import { Color, BtnSelect } from '@/components';
  import { itemImage, normalize, categoryOptions } from '@/utils';
  import { mapState } from 'vuex';
  import { appBase } from '@/utils/basePath';
  import { isExtremaCockpit } from '@/utils/threeTCockpit';
  import { partWeight } from '@/utils/weight';
  import { weightDelta } from '@/utils/weightDelta';
  const brands = ['enve', 'avona', '3t', 'specialized', 'colnago', 'bmc', 'baum', 'seka', 'ridley', 'aurum', 'factor', 'festka', 'open', 'pinarello', 'sram', 'shimano', 'campagnolo'];

  const tyreLogos = {"pirelli": "https://www.velodrom.cc/cdn/shop/collections/b3fa7d07ba25bd7c64faf8d4d50ca4ac_217479df-9604-4228-b120-876193fcc3fd.png?v=1788418167&width=960", "continental": "https://www.velodrom.cc/cdn/shop/collections/4b977410672fcc98636d9b346972d883_6b39ba20-5f93-4e1a-99cd-4939bd9c4804.png?v=1788417757&width=960", "vittoria": "https://www.velodrom.cc/cdn/shop/collections/brand-vittoria_2ffb1292-6d88-423a-8387-505b86541f3b.png?v=1788418628&width=960", "schwalbe": "https://www.velodrom.cc/cdn/shop/collections/brand-schwalbe_d1185253-1375-4887-8aba-6dc1fe860623.png?v=1788418240&width=960", "panaracer": "https://www.velodrom.cc/cdn/shop/collections/b3864c810defc1fe06185048adb73256_da914072-1eb1-47fd-83ed-11c15d08896c.png?v=1788418053&width=960"};

  export default {
    components: { Color, BtnSelect },
    props: {
      value: null,
      type: String,
      // Owned by the builder so the phone layout can show it over the bike.
      category: { type: String, default: 'All' },
      // The build's entry for this step ({ item, color, tyreWidth, props }), for the weight/price comparison.
      selection: { type: Object, default: null },
      items: {
        type: Array,
        default: () => []
      }
    },
    data: () => ({
      search: '',
      unavailable: {}
    }),
    watch: {
      items() {
        this.search = '';
        this.$emit( 'update:category', 'All' );
        this.scrollToSelected();
      }
    },
    mounted() {
      this.scrollToSelected();
    },
    methods: {
      isExtremaCockpit,
      isIncluded( name ) {
        return name === 'SEKA Gladius' || isExtremaCockpit( name );
      },
      // Compare each card at the price it shows (its cheapest finish) with the chosen part.
      // "No pedals"/"No cages" cards get no line: dropping a part is not a like-for-like swap.
      delta( item, index, minPrice ) {
        const selected = this.selectedSide;
        if ( !selected || index === this.value || item.noCages || item.noPedals ) return null;
        if ( !( minPrice > 0 ) || this.isIncluded( item.name )) return null;
        const cheapest = item.colors.findIndex( a => (( a.color && a.color.price ) || a.price || 0 ) === minPrice );
        const weight = partWeight({ ...this.selection, item, color: Math.max( cheapest, 0 ) });
        return weight && weightDelta({ grams: weight.grams, price: minPrice }, selected );
      },
      scrollToSelected() {
        // Position once after Vue has rendered the selection, before paint.
        // Delayed retries jump the list after navigation or user scrolling.
        this.$nextTick(() => {
          if ( this._isDestroyed ) return;
          const group = this.$refs.group;
          if ( !group ) return;
          group.scrollTop = 0;
          group.scrollLeft = 0;
          const selected = group.querySelector( '.bike-item.selected' );
          if ( !selected ) return;
          const gr = group.getBoundingClientRect();
          const sr = selected.getBoundingClientRect();
          if (group.scrollWidth > group.clientWidth && (sr.left < gr.left || sr.right > gr.right)) {
            group.scrollLeft += sr.left - gr.left - 12;
          }
          if ( sr.top < gr.top || sr.bottom > gr.bottom ) {
            group.scrollTop += ( sr.top + sr.height / 2 ) - ( gr.top + gr.height / 2 );
          }
        });
      }
    },
    computed: {
      ...mapState([ 'specialBuilds' ]),
      filterOptions() {
        return categoryOptions( this.type );
      },
      visibleList() { return this.list.filter(item => !item.hide); },
      // What the chosen part weighs and costs in the build, as the footer total counts it.
      selectedSide() {
        const entry = this.selection;
        if ( !entry || !entry.item ) return null;
        const weight = partWeight( entry );
        const choice = entry.item.colors[ entry.color ] || {};
        const price = ( choice.color && choice.color.price ) || choice.price || 0;
        const none = entry.item.noCages || entry.item.noPedals;
        if ( !weight || !( price > 0 || none ) || this.isIncluded( entry.item.name )) return null;
        return { grams: weight.grams, price: none ? 0 : price };
      },
      list() {
        let search = normalize( this.search );
        return this.items.map(( item, index ) => {
          // Alternative-fork siblings repeat the finishes; list each finish once.
          const colors = item.colors.map( a => a.color ).filter( c => !c.forkOption );
          const image = itemImage( item );
          const name = (item.name || '').toLowerCase().replace(/^conti /, 'continental ');
          // Pedal cards show the product photo of the pair, not a brand logo.
          const brand = this.type === 'Pedals' ? null : [...brands, ...Object.keys(tyreLogos), 'wtb', 'michelin'].find(brand => name.startsWith(brand + ' '));
          const brandLogo = brand ? tyreLogos[brand] || `${appBase}/img/brands/${brand}.${['wtb', 'michelin'].includes(brand) ? 'svg' : 'png'}` : null;
          const prices = item.colors
            .map( a => ( a.color && a.color.price ) || a.price || 0 )
            .filter( p => p > 0 );
          const minPrice = prices.length ? Math.min( ...prices ) : 0;

          // Tyres and bottle cages are sold and rendered as a PAIR, and the
          // stored price is the pair total — show it as "2 × unit € = total €".
          // Half to the cent (so 139.98 -> 69.99, not 70), trailing zeros dropped.
          const pair = !item.noCages && [ 'Tyres', 'Bottle Cages' ].includes( this.type );
          const unitPrice = pair ? +( minPrice / 2 ).toFixed( 2 ) : minPrice;

          // Category Filter Logic
          let categoryMatch = true;
          if (this.filterOptions.length && this.category !== 'All' && !item.noPedals) {
              // Item category might be null if not yet migrated in local state, but DB defaults to Road.
              // We should assume Road if missing, or exact match.
              const itemCat = item.category || 'Road'; 
              // Normalize for comparison
              categoryMatch = itemCat.toLowerCase() === this.category.toLowerCase();
          }

          return {
            ...item,
            index,
            thumb: !brandLogo && !!image.thumb,
            brand, brandLogo,
            src: this.type === 'Pedals' ? image.front || image.thumb || image.src : this.type === 'Bars/Stem' ? image.thumb || image.src : this.type === 'Groupset' ? image.front || image.thumb : this.type === 'Saddles' ? image.src || image.thumb : brandLogo ? image.src || image.front || image.thumb : image.thumb || image.front || image.src,
            hide: !categoryMatch || ! normalize( item.name || '' ).includes( search ),
            delta: this.delta( item, index, minPrice ),
            specialBuild: this.type === 'Frameset' && this.specialBuilds.find( s => {
              return s.frameset === item.id;
            }),
            info: {
              colors: colors.slice( 0, 3 ),
              more: colors.length > 3 ? colors.length - 3 : 0,
              price: minPrice,
              priceFrom: prices.length > 1 && Math.max( ...prices ) > minPrice,
              pair,
              unitPrice
            }
          };
        });
      }
    }
  };
</script>

<style lang="scss">
  .builder-nav--items {
    padding-top: 60px;
  }
  .builder-nav--items > header {
    position: absolute;
    top: 0; left: 0;
    width: 100%;
    height: 60px;
    display: flex;
    align-items: center;
    padding: 8px;
    z-index: 2;
    background: white; /* Ensure it covers scrolling content */
  }
  .builder-nav--items > header > input {
    flex: 1 1 auto;
    border-radius: 4px;
    padding: 4px 8px;
  }
  /*.builder-nav--items > header > input:focus {
    outline: 0;
  }*/
  .bike-item {

    display: flex;
    flex-direction: column;
    position: relative;
    border-left: 5px solid transparent;
    border-bottom: 1px solid var(--v-light-base);
    transition: background-color 120ms ease;
    text-decoration: none;
    height: 240px;

    .bike-item-image {
      flex: 1 0 auto;
      background-repeat: no-repeat;
      background-position: center center;
      background-size: contain;
      margin: 10px 0 60px 0;
    }
    &:not(.thumb) .bike-item-image {
      transform: scale(.75);
    }
    .bike-item-icon {
      position: absolute;
      top: 10px;
      right: 10px;
    }
    .bike-item-info-name {
      position: absolute;
      left: 0; bottom: 24px; right: 120px;
      padding: 10px 10px 0;
    }
    .bike-item-info-price {
      position: absolute;
      left: 0; bottom: 0; right: 120px;
      padding: 2px 10px 8px;
      font-size: .8rem;
      font-weight: 600;
      color: var(--bb-primary);
    }
    .bike-item-info-colors {
      position: absolute;
      right: 10px; bottom: 47px;
    }
    &.thumb .bike-item-image {
      margin: 0 0 60px 0;
    }
    &.selected {
      background-color: #f4f4f4;
      border-left: 5px solid var(--bb-primary);
    }
    &:hover {

      background-color: var(--bb-secondary);
      color: white;

      .bike-item-info-name, .bike-item-info-colors, .bike-item-info-price {
        color: white !important;
      }
    }
    .bike-item-select {
      position: absolute;
      bottom: 10px;
      right: 10px;
      border: 1px solid var(--v-light-base) !important;
    }
  }
  @media ( max-width: 1024px ) {
    .builder-nav--items > div {
      overflow-y: hidden !important;
    }
    .builder-nav--items > div {
      white-space: nowrap;
    }
    .bike-item {

      display: inline-flex;
      width: 350px;
      height: 100%;
      border-bottom: 0;
      border-left: 0;
      border-right: 1px solid var(--v-light-base);
      border-top: 5px solid transparent;
      vertical-align: top;

      .bike-item-info-name {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }

      &.selected {
        border-left: 0;
        border-top: 5px solid var(--bb-primary);
      }
    }
  }
  /* Phones: bespoke-style swipeable cards — image on top, name and
     swatches stacked below, snap scrolling. */
  @media ( max-width: 600px ) {
    .builder-nav--items {
      padding-top: 0;
    }
    .builder-nav--items > header {
      display: none;
    }
    .builder-nav--items > div {
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch;
    }
    .bike-item {

      width: 46vw;
      min-width: 168px;
      max-width: 220px;
      padding: 10px 14px 12px;
      scroll-snap-align: start;
      border-top-width: 3px;

      &.selected {
        border-top-width: 3px;
      }

      .bike-item-image {
        flex: 1 1 auto;
        margin: 0;
        transform: none !important;
        background-position: center;
        background-size: contain;
      }
      &.thumb .bike-item-image {
        margin: 0;
        background-position: center;
        background-size: contain;
      }
      .bike-item-info-name {
        position: static;
        padding: 8px 0 2px;
        font-size: .78rem !important;
        line-height: 1.05rem;
        letter-spacing: .02em;
        text-transform: uppercase;
        white-space: normal;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        min-height: 2.2rem;
      }
      .bike-item-info-price {
        position: static;
        padding: 0 0 4px;
        font-size: .72rem;
      }
      .bike-item-info-colors {
        position: static;
        padding-bottom: 2px;
      }
      .bike-item-icon {
        top: 8px;
        right: 8px;
      }
      .bike-item-select {
        display: none !important;
      }
    }
  }
</style>
